/**
 * DraftingActivityPane - what has happened to this document, as a pane body.
 *
 * The rows are `shared/activity/ActivityPane` - the ONE chronology surface, so
 * a draft's history reads exactly like a matter's: a hairline rail with a
 * glyph disc on it, the event as a quiet well beside it, the actor with their
 * avatar and a relative timestamp underneath. Nothing here re-implements any
 * of that; see CLAUDE.md §6.8.
 *
 * What this file adds is only what the pane needs: the actor avatars turned
 * ON (a drafted document is worked by a handful of named people, so the disc
 * is a face rather than the wall of identical initials the flag exists to
 * avoid) and the toolbar turned OFF, because the refresh control sits in the
 * PANE HEADER on this surface - see `useDraftingActivity`.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import PaneBody from '../../shared/surface/PaneBody';
import ActivityPane from '../../shared/activity/ActivityPane';
import type { DraftingActivityState } from './useDraftingActivity';

interface Props {
  activity: DraftingActivityState;
}

const DraftingActivityPane: React.FC<Props> = ({ activity }) => {
  const { t } = useTranslation();
  const { entries, loading, hasMore, refresh, loadMore } = activity;

  return (
    <PaneBody>
      <ActivityPane
        className="flex-1 min-h-0 px-4 py-3"
        entries={entries}
        loading={loading}
        onRefresh={refresh}
        hasMore={hasMore}
        onLoadMore={loadMore}
        // Inside a pane the list is already nested in a glass card, so the rows
        // are wells rather than a second layer of cards.
        density="pane"
        showActorAvatar
        hideToolbar
        emptyState={t('Generating, editing and linking this document all appear here.')}
      />
    </PaneBody>
  );
};

export default DraftingActivityPane;
